import { z } from 'zod';
import superjson from 'superjson';
import { Selectable } from 'kysely';
import { SessionThemes } from '../../helpers/schema';

// Theme tokens schema matching the user's system prompt output
export const themeTokensSchema = z.object({
  colors: z.object({
    // Core
    primary: z.string(),
    primaryForeground: z.string().optional(),
    secondary: z.string(),
    secondaryForeground: z.string().optional(),
    accent: z.string(),
    accentForeground: z.string().optional(),
    // Backgrounds & surfaces
    background: z.string(),
    foreground: z.string().optional(),
    surface: z.string(),
    surfaceForeground: z.string().optional(),
    card: z.string().optional(),
    cardForeground: z.string().optional(),
    sidebar: z.string().optional(),
    sidebarForeground: z.string().optional(),
    popup: z.string().optional(),
    popupForeground: z.string().optional(),
    muted: z.string().optional(),
    mutedForeground: z.string().optional(),
    // Text (legacy compat)
    text: z.string(),
    textMuted: z.string(),
    // Borders & inputs
    border: z.string(),
    mutedBorder: z.string().optional(),
    input: z.string().optional(),
    ring: z.string().optional(),
    // Status
    error: z.string(),
    errorForeground: z.string().optional(),
    success: z.string(),
    successForeground: z.string().optional(),
    warning: z.string(),
    warningForeground: z.string().optional(),
    info: z.string().optional(),
    infoForeground: z.string().optional(),
    // Charts
    chart1: z.string().optional(),
    chart2: z.string().optional(),
    chart3: z.string().optional(),
    chart4: z.string().optional(),
    chart5: z.string().optional(),
  }),
  typography: z.object({
    fontFamily: z.string(),
    headingFontFamily: z.string(),
    baseFontSize: z.string(),
    lineHeight: z.string(),
    headingLineHeight: z.string(),
    fontWeight: z.string().optional(),
    headingFontWeight: z.string().optional(),
    fontStyle: z.string().optional(),
  }),
  spacing: z.object({
    xs: z.string(),
    sm: z.string(),
    md: z.string(),
    lg: z.string(),
    xl: z.string(),
  }),
  borderRadius: z.object({
    none: z.string(),
    sm: z.string(),
    md: z.string(),
    lg: z.string(),
    full: z.string(),
  }),
  shadows: z.object({
    sm: z.string(),
    md: z.string(),
    lg: z.string(),
  }),
});

export type ThemeTokens = z.infer<typeof themeTokensSchema>;

export const schema = z.object({
  sessionId: z.string().optional(), // Optional - if not provided, saves as global theme
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  tokens: themeTokensSchema,
  darkTokens: themeTokensSchema.optional(),
  imagePrompt: z.string().optional(),
  setActive: z.boolean().optional().default(false),
  saveGlobal: z.boolean().optional().default(false), // Save as global user theme
  isPublic: z.boolean().optional().default(false), // Make theme publicly accessible
});

export type InputType = z.infer<typeof schema>;

export type OutputType = {
  theme: Selectable<SessionThemes>;
};

export const createTheme = async (input: InputType, init?: RequestInit): Promise<OutputType> => {
  const result = await fetch('/_api/themes/create', {
    method: 'POST',
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    body: superjson.stringify(input),
  });
  if (!result.ok) {
    const errorObject = superjson.parse<{ error: string }>(await result.text());
    throw new Error(errorObject.error);
  }
  return superjson.parse<OutputType>(await result.text());
};
